import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import NavbarAr from "@/components/NavbarAr";
import Footer from "@/components/Footer";
import FooterAr from "@/components/FooterAr";
import Brand from "@/components/Brand";

// Terms of Service — bilingual (EN / AR). DRAFT for owner review.
//
// Prepared February 2026. This document is a professional draft
// covering only V1 website/application matters. It has not yet been
// legally approved and is displayed on the site under a visible
// "Draft — pending owner review" notice.
//
// Content deliberately excludes: paid subscriptions, commercial
// guarantees, statutory legal waivers, automatic renewals,
// arbitration clauses, ownership transfers, and any business model
// not already approved. All references to data handling defer to
// the Privacy Policy; account-closure lifecycle defers to the
// Account Deletion page (RC-V47 30-day scheduled deletion).

const CONTROLLER_EN =
    "Masirat Al-Hayat for Entertainment (مسيرة الحياة للترفيه), operator of CrewZNatioN";
const CONTROLLER_AR = "مسيرة الحياة للترفيه، المُشغِّل لعلامة CrewZNatioN";
const SUPPORT_EMAIL = "admin@crewznation.com";
const EFFECTIVE_EN = "Draft — pending owner review (Feb 2026)";
const EFFECTIVE_AR = "مسوّدة — قيد المراجعة من المالك (فبراير ٢٠٢٦)";

const SECTIONS = [
    {
        id: "acceptance",
        en: {
            heading: "1. Acceptance of these Terms",
            body: [
                <>These Terms of Service (the &quot;Terms&quot;) govern access to and use of the <Brand /> website at crewznation.com, the CrewZNatioN mobile application, and any associated community, event, Garage, Feed, Hub/Marketplace, invitation, QR/pass and support services (together, the &quot;Service&quot;). By creating an account, using the Service, or attending a <Brand /> event, the user agrees to these Terms and to the <Link to="/privacy" className="text-[var(--crewz-gold)] hover:underline">Privacy Policy</Link>.</>,
                <>If the user does not agree, the user should not use the Service or attend a <Brand /> event as an authenticated participant.</>,
            ],
        },
        ar: {
            heading: "١. قبول هذه الشروط",
            body: [
                <>تحكم شروط الخدمة هذه (&quot;الشروط&quot;) الوصول إلى موقع <Brand /> على crewznation.com، وتطبيق CrewZNatioN للأجهزة الجوّالة، وكل ما يرتبط بهما من خدمات المجتمع، والفعاليات، والجراج، والتغذية (Feed)، والهَب/السوق (Hub/Marketplace)، والدعوات، ورموز QR/التصاريح، والدعم (يُشار إليها مجتمعةً بـ &quot;الخدمة&quot;). ويُعدّ إنشاء الحساب، أو استخدام الخدمة، أو حضور أي فعاليةٍ من فعاليات <Brand />، موافقةً من المستخدم على هذه الشروط وعلى <Link to="/ar/privacy" className="text-[var(--crewz-gold)] hover:underline">سياسة الخصوصية</Link>.</>,
                <>وفي حال عدم الموافقة، ينبغي عدم استخدام الخدمة أو حضور فعاليات <Brand /> بصفةٍ مشاركةٍ مسجَّلة.</>,
            ],
        },
    },
    {
        id: "eligibility",
        en: {
            heading: "2. Eligibility (18+ requirement)",
            body: [
                <>The Service is intended for persons aged <strong>18 and above</strong>. By creating an account or using the Service, the user represents that they meet this age requirement. Accounts identified as belonging to a person under 18 are restricted or removed, and any associated personal data is handled as described in the Privacy Policy.</>,
                <>Access to certain features (such as the Vehicle-Owner Garage) may require additional eligibility criteria described at the point of use.</>,
            ],
        },
        ar: {
            heading: "٢. الأهلية (شرط ١٨ عامًا فأكثر)",
            body: [
                <>الخدمة مخصّصة للأشخاص الذين تبلغ أعمارهم <strong>١٨ عامًا فأكثر</strong>. وبإنشاء الحساب أو استخدام الخدمة، يُقرّ المستخدم بأنه مستوفٍ لشرط السن. وتُقيَّد الحسابات التي يتبيّن أن أصحابها دون الثامنة عشرة أو تُزال، وتُعالَج أي بياناتٍ شخصيةٍ مرتبطةٍ بها وفق ما تنصّ عليه سياسة الخصوصية.</>,
                <>وقد يستلزم الوصول إلى ميزاتٍ معيّنة (مثل جراج صاحب المركبة) شروط أهليةٍ إضافية توضَّح عند نقطة الاستخدام.</>,
            ],
        },
    },
    {
        id: "account",
        en: {
            heading: "3. Account responsibilities",
            body: [
                <>The user is responsible for the accuracy of the information provided at registration, for keeping account credentials confidential, and for all activity that occurs under the account. The user should notify <Brand /> promptly of any suspected unauthorised access.</>,
                <>Only one active account per person is permitted, unless <Brand /> has expressly authorised additional accounts.</>,
            ],
        },
        ar: {
            heading: "٣. مسؤوليات الحساب",
            body: [
                <>يتحمّل المستخدم مسؤولية دقة المعلومات المُقدَّمة عند التسجيل، والمحافظة على سرّية بيانات الدخول، وجميع النشاطات التي تجري من خلال الحساب. وعليه إبلاغ <Brand /> فورًا عند الاشتباه بأي وصولٍ غير مصرَّح به.</>,
                <>ولا يُسمح إلا بحسابٍ نشِطٍ واحدٍ لكل شخص، ما لم تكن <Brand /> قد أذنت صراحةً بحساباتٍ إضافية.</>,
            ],
        },
    },
    {
        id: "roles",
        en: {
            heading: "4. Guest and Garage Owner roles",
            body: [
                <>The Service distinguishes between two member roles:</>,
            ],
            list: [
                <><strong>Guest</strong> — a general member of the community with access to the standard features made available to guests.</>,
                <><strong>Garage Owner</strong> — a member who has been approved to publish a vehicle profile inside the Garage. This role is offered only after eligibility review and requires ownership of, or authorised access to, the vehicle presented.</>,
            ],
            footer: [
                <>The two roles carry different responsibilities. Garage Owners are additionally accountable for the accuracy and lawfulness of the vehicle information and media they publish.</>,
            ],
        },
        ar: {
            heading: "٤. دور الضيف ودور صاحب الجراج",
            body: [
                <>تُميّز الخدمة بين نوعين من الأعضاء:</>,
            ],
            list: [
                <><strong>الضيف (Guest)</strong> — عضو عام في المجتمع يصل إلى الميزات الاعتيادية المتاحة للضيوف.</>,
                <><strong>صاحب الجراج (Garage Owner)</strong> — عضو اعتُمِد لنشر ملف مركبةٍ داخل الجراج. ويُمنح هذا الدور بعد مراجعة الأهلية، ويشترط ملكية المركبة المعنيّة أو صلاحيةً موثَّقةً للتصرّف فيها.</>,
            ],
            footer: [
                <>ويحمل كلّ دورٍ مسؤولياتٍ مختلفة. ويتحمّل أصحاب الجراج مسؤوليةً إضافيةً عن دقة معلومات المركبة ومشروعية الوسائط التي ينشرونها.</>,
            ],
        },
    },
    {
        id: "truthful",
        en: {
            heading: "5. Truthful vehicle and profile information",
            body: [
                <>Vehicle specifications, ownership status, modifications history, event participation and profile information must be truthful and current. Misrepresentation — including impersonation, fabricated ownership, or falsified specifications — is grounds for removal of the listing, restriction of the account, and, where applicable, referral to authorities.</>,
            ],
        },
        ar: {
            heading: "٥. صدق معلومات المركبة والملف الشخصي",
            body: [
                <>يجب أن تكون مواصفات المركبة، وحالة ملكيتها، وسجل تعديلاتها، والمشاركة في الفعاليات، ومعلومات الملف الشخصي صحيحةً ومحدَّثة. ويُعدّ التضليل — بما يشمل انتحال الشخصية، وادّعاء ملكيةٍ غير حقيقية، وتزوير المواصفات — سببًا لإزالة الإعلان، وتقييد الحساب، وإحالة الأمر إلى الجهات المختصّة عند الاقتضاء.</>,
            ],
        },
    },
    {
        id: "conduct",
        en: {
            heading: "6. Acceptable conduct",
            body: [
                <>Members are expected to conduct themselves in line with the standards published on the <Link to="/rules" className="text-[var(--crewz-gold)] hover:underline">Rules &amp; Regulations</Link> page. These include respecting Saudi law and traffic law at all times, driving and riding responsibly, respecting organisers, staff, security personnel and venues, and representing <Brand /> responsibly both in the community and in public.</>,
            ],
        },
        ar: {
            heading: "٦. السلوك المقبول",
            body: [
                <>يُتوقَّع من الأعضاء التصرّف بما يتفق مع المبادئ المنشورة في صفحة <Link to="/ar/rules" className="text-[var(--crewz-gold)] hover:underline">الأنظمة واللوائح</Link>. ويشمل ذلك احترام أنظمة المملكة وأنظمة المرور في جميع الأوقات، وقيادة المركبات بمسؤولية، واحترام المنظّمين وطاقم العمل ورجال الأمن والأماكن المستضيفة، وتمثيل <Brand /> بشرفٍ ومسؤولية داخل المجتمع وفي الفضاء العام.</>,
            ],
        },
    },
    {
        id: "safety",
        en: {
            heading: "7. Vehicle-community safety expectations",
            body: [
                <>The Service exists to support a culture of responsible enthusiasm. Reckless driving, street racing, burnouts on public or private grounds, revving in restricted or residential areas, and any behaviour that endangers persons, property or the community is strictly outside the scope of what the Service or a <Brand /> gathering permits. Such behaviour is grounds for removal from an event, restriction of the account, and referral to authorities as appropriate.</>,
            ],
        },
        ar: {
            heading: "٧. توقّعات السلامة داخل مجتمع المركبات",
            body: [
                <>وُجدت الخدمة لدعم ثقافة الشغف المسؤول. ولا تسمح الخدمة ولا فعاليات <Brand /> بالقيادة المتهوّرة، ولا سباقات الشوارع، ولا التفحيط في الأماكن العامة أو الخاصة، ولا رفع صوت المحرّك في المناطق المُقيّدة أو السكنية، ولا أي سلوكٍ يُعرّض الأشخاص أو الممتلكات أو المجتمع للخطر. وتُعدّ هذه الأفعال سببًا لإخراج المخالف من الفعالية، وتقييد الحساب، وإحالة الأمر إلى الجهات المختصّة عند الاقتضاء.</>,
            ],
        },
    },
    {
        id: "content",
        en: {
            heading: "8. User-uploaded content and rights",
            body: [
                <>Members retain ownership of the original text, photos, videos and GIF files they upload (&quot;Member Content&quot;). By uploading Member Content, the member grants <Brand /> a non-exclusive, worldwide, royalty-free licence to host, store, reproduce, adapt technically (for example, resize, transcode, thumbnail), and display that content within the Service and for the purpose of operating and promoting the Service, in line with the visibility setting the member has selected. Promotional reuse of a member&apos;s identifiable content outside the feature it was posted to requires a separate, approved consent.</>,
                <>Members must upload only content they have the right to publish. Uploading third-party photos, videos, GIFs, logos or other protected material without proper authority is prohibited.</>,
            ],
        },
        ar: {
            heading: "٨. المحتوى الذي يرفعه المستخدم وحقوقه",
            body: [
                <>يحتفظ العضو بملكية النصوص والصور ومقاطع الفيديو وملفات GIF الأصلية التي يرفعها (&quot;محتوى العضو&quot;). وبرفعه لهذا المحتوى، يمنح العضو <Brand /> ترخيصًا غير حصريٍّ، عالميًّا، ومجّانيًّا لاستضافته وتخزينه ونسخه وتعديله تقنيًّا (كإعادة تحجيمه أو تحويل ترميزه أو توليد صور مصغّرة له)، وعرضه داخل الخدمة، ولأغراض تشغيل الخدمة والترويج لها، وذلك وفق إعداد الظهور الذي اختاره العضو. ويستلزم أي استخدامٍ ترويجيٍّ للمحتوى المُعرِّف بصاحبه خارج الميزة التي نُشِر فيها الحصول على موافقةٍ منفصلةٍ ومعتَمدة.</>,
                <>ولا يجوز للعضو رفع أي محتوى لا يملك حق نشره. ويُحظَر رفع صور أو مقاطع فيديو أو ملفات GIF أو شعارات أو أي موادّ محميةٍ لأطرافٍ ثالثة دون صلاحيةٍ مناسبة.</>,
            ],
        },
    },
    {
        id: "events",
        en: {
            heading: "9. Events, invitations and QR passes",
            body: [
                <>Applying for or accepting an invitation to a <Brand /> event, receiving a QR pass, and presenting that pass at entry constitute an undertaking to comply with the event&apos;s published terms, with venue rules, and with the standards on the Rules &amp; Regulations page. A QR pass is personal to the invited member and is not transferable unless <Brand /> has expressly authorised transfer for a specific event.</>,
                <>Entry to any <Brand /> event is at the discretion of <Brand />. Access may be denied or withdrawn at any point where the event&apos;s standards, safety, or venue rules require it.</>,
            ],
        },
        ar: {
            heading: "٩. الفعاليات والدعوات ورموز QR",
            body: [
                <>يُعدّ التقدّم للفعالية أو قبول الدعوة إليها، والحصول على رمز QR/تصريح الدخول، وإبرازه عند الدخول، تعهّدًا بالالتزام بشروط الفعالية المعلَنة، وبأنظمة المكان المستضيف، وبالمبادئ المنشورة في صفحة الأنظمة واللوائح. ويُعدّ تصريح QR شخصيًّا للعضو المدعوّ، وغير قابلٍ للنقل ما لم تأذن <Brand /> بذلك صراحةً لفعاليةٍ محدّدة.</>,
                <>ويخضع الدخول إلى أي فعاليةٍ من فعاليات <Brand /> لتقدير <Brand />. ويجوز منع الوصول أو سحبه في أي وقتٍ إذا اقتضت ذلك معايير الفعالية أو السلامة أو أنظمة المكان.</>,
            ],
        },
    },
    {
        id: "marketplace",
        en: {
            heading: "10. Hub / Marketplace responsibilities",
            body: [
                <>Where the Service offers a Hub or Marketplace feature, listings are posted by members. Each seller is solely responsible for the accuracy of their listing, for the lawfulness of what they list, and for any transaction they arrange with another member. <Brand /> in V1 is not the merchant of record and does not act as escrow, arbiter or guarantor of member-to-member listings.</>,
                <>Prohibited listings include (without limitation): stolen or misappropriated items, unsafe or non-roadworthy modifications marketed as safe, counterfeit branded goods, and any items the sale of which is restricted by Saudi law. Members who buy through the Marketplace are expected to verify items independently before completing a transaction.</>,
            ],
        },
        ar: {
            heading: "١٠. مسؤوليات الهَب/السوق",
            body: [
                <>حيثما تُتاح ميزة الهَب/السوق، تُنشر الإعلانات من قِبل الأعضاء أنفسهم. ويتحمّل كل بائعٍ وحده مسؤولية دقة إعلانه، ومشروعية ما ينشره، وأي معاملةٍ يبرمها مع عضوٍ آخر. ولا تُعدّ <Brand /> في الإصدار الأول (V1) البائعَ المُسجَّل، ولا تعمل بوصفها ضامنةً أو حَكَمًا أو أمينةً للعُهدة في المعاملات بين الأعضاء.</>,
                <>وتشمل الإعلانات المحظورة (على سبيل المثال لا الحصر): الأشياء المسروقة أو المستولى عليها، والتعديلات غير الآمنة أو غير الصالحة للسير المسوَّقة على أنها آمنة، والسلع المقلَّدة، وأي أشياء يُقيّد النظام السعودي بيعها. ويُتوقَّع من المشترين من خلال السوق التحقّق من العرض باستقلاليةٍ قبل إتمام أي معاملة.</>,
            ],
        },
    },
    {
        id: "prohibited",
        en: {
            heading: "11. Prohibited misuse",
            body: [<>The following are prohibited in and around the Service:</>],
            list: [
                <>bypassing, disabling or interfering with security, moderation, rate-limits or authentication controls;</>,
                <>scraping, harvesting, or automated collection of the Service&apos;s content or member data without written consent;</>,
                <>uploading malware, viruses, or code intended to harm the Service, its infrastructure, or its members;</>,
                <>impersonation of another person, of <Brand /> staff, or of any organisation;</>,
                <>harassment, threats, hate speech, targeted intimidation, doxxing, or non-consensual sharing of another person&apos;s private data or media;</>,
                <>promotion of illegal activity or of driving/riding behaviour that endangers persons or property;</>,
                <>using the Service or an event as a channel to commit fraud or otherwise deceive members.</>,
            ],
        },
        ar: {
            heading: "١١. الاستخدامات المحظورة",
            body: [<>يُحظَر داخل الخدمة وحول محيطها ما يلي:</>],
            list: [
                <>تجاوز ضوابط الأمن أو الرقابة على المحتوى أو حدود الاستخدام أو المصادقة، أو تعطيلها، أو التلاعب بها؛</>,
                <>حصد المحتوى أو بيانات الأعضاء من الخدمة أو جمعها آليًّا دون موافقةٍ خطية؛</>,
                <>رفع برامج ضارّة، أو فيروسات، أو أي شيفرةٍ تهدف إلى الإضرار بالخدمة أو بنيتها التحتية أو أعضائها؛</>,
                <>انتحال شخصية شخصٍ آخر، أو أحد منسوبي <Brand />، أو أي جهة؛</>,
                <>التحرّش، والتهديد، وخطاب الكراهية، والترهيب الموجَّه، وإفشاء البيانات الشخصية دون إذنٍ، ومشاركة الوسائط الخاصة لشخصٍ آخر دون موافقته؛</>,
                <>الترويج لأي نشاطٍ غير نظامي، أو لسلوكياتٍ في القيادة تُعرّض الأشخاص أو الممتلكات للخطر؛</>,
                <>استخدام الخدمة أو أي فعاليةٍ منها قناةً للاحتيال أو خداع الأعضاء.</>,
            ],
        },
    },
    {
        id: "actions",
        en: {
            heading: "12. Suspension, deactivation and deletion",
            body: [
                <><Brand /> may restrict, suspend, deactivate or remove an account, listing, post, comment, media item, event invitation or QR pass where reasonably necessary to enforce these Terms, the Rules &amp; Regulations, applicable law, event safety, venue rules, or a lawful order from a competent authority.</>,
                <>Members may deactivate the account at any time from inside the app; deactivation is reversible indefinitely. Members may also request permanent deletion. Permanent deletion is scheduled, not immediate: it enters a <strong>30-day cancellation window</strong> during which the member can sign back in and cancel. On day 30 the cancellation window closes and the account and its associated personal data are permanently deleted or irreversibly anonymized, subject to the retention exceptions described in the <Link to="/privacy" className="text-[var(--crewz-gold)] hover:underline">Privacy Policy</Link> and on the <Link to="/account-deletion" className="text-[var(--crewz-gold)] hover:underline">Account Deletion</Link> page.</>,
            ],
        },
        ar: {
            heading: "١٢. الإيقاف، وإلغاء التنشيط، والحذف",
            body: [
                <>يجوز لـ <Brand /> تقييد أو إيقاف أو إلغاء تنشيط أو إزالة أي حسابٍ أو إعلانٍ أو منشورٍ أو تعليقٍ أو وسائطٍ أو دعوة فعالية أو تصريح QR متى ما كان ذلك ضروريًّا بصورةٍ معقولة لإنفاذ هذه الشروط، أو الأنظمة واللوائح، أو الأنظمة السارية، أو متطلّبات سلامة الفعالية، أو قواعد المكان المستضيف، أو أي أمرٍ نظاميٍّ من جهةٍ مختصّة.</>,
                <>ويجوز للعضو إلغاء تنشيط حسابه في أي وقتٍ من داخل التطبيق؛ وهذا الإلغاء قابلٌ للاسترجاع دون مدةٍ محدَّدة. كما يجوز للعضو طلب الحذف النهائي. والحذف النهائي مُجدوَل لا فوري: يدخل الحساب في <strong>مهلة إلغاءٍ مدّتها ٣٠ يومًا</strong> يمكن للعضو خلالها تسجيل الدخول وإلغاء الحذف. وفي اليوم الثلاثين تُغلَق مهلة الإلغاء، ويُحذَف الحساب وبياناته الشخصية المرتبطة به بشكلٍ نهائي أو يُجهَّل بشكلٍ لا يمكن عكسه، وفق استثناءات الاحتفاظ الواردة في <Link to="/ar/privacy" className="text-[var(--crewz-gold)] hover:underline">سياسة الخصوصية</Link> وفي صفحة <Link to="/ar/account-deletion" className="text-[var(--crewz-gold)] hover:underline">حذف الحساب</Link>.</>,
            ],
        },
    },
    {
        id: "ip",
        en: {
            heading: "13. Intellectual property and CrewZNatioN branding",
            body: [
                <>The <Brand /> name, the CrewZNatioN wordmark, associated logos, the &quot;The Gathering&quot; wordmark, event marks, the Service&apos;s design system, editorial copy, and other material owned by <Brand /> or licensed to it are protected by intellectual-property law. No part of these marks or materials may be reproduced, adapted, displayed on merchandise, or used to imply an official association with <Brand /> without prior written consent.</>,
            ],
        },
        ar: {
            heading: "١٣. الملكية الفكرية وعلامة CrewZNatioN",
            body: [
                <>يخضع اسم <Brand />، وشعار CrewZNatioN، والشعارات المرتبطة به، وعلامة &quot;The Gathering&quot;، وعلامات الفعاليات، ومنظومة تصميم الخدمة، والمحتوى التحريري، وسائر ما تملكه <Brand /> أو ما رُخِّص لها لاستخدامه، لأنظمة الملكية الفكرية. ولا يجوز إعادة إنتاج أي جزءٍ من هذه العلامات أو الموادّ، أو تعديله، أو استخدامه على منتجات، أو الإيحاء بأي ارتباطٍ رسميٍّ بـ <Brand />، دون الحصول على موافقةٍ خطيةٍ مسبقة.</>,
            ],
        },
    },
    {
        id: "availability",
        en: {
            heading: "14. Availability and limitation language",
            body: [
                <>The Service is provided on an &quot;as available&quot; basis. Features, event invitations, availability windows and third-party integrations may be modified, paused, or discontinued to keep the Service secure and lawful. <Brand /> does not guarantee that the Service will be uninterrupted, error-free, or continuously compatible with every device.</>,
                <>Nothing in these Terms limits any right that Saudi law grants to a user and that cannot be limited by contract.</>,
            ],
        },
        ar: {
            heading: "١٤. صياغة الإتاحة والحدود",
            body: [
                <>تُقدَّم الخدمة على أساس &quot;كما هي متاحة&quot;. وقد تُعدَّل الميزات، ودعوات الفعاليات، وفترات الإتاحة، وعمليات التكامل مع الأطراف الثالثة، أو تُوقَف مؤقتًا، أو يُوقَف العمل بها، بما يحفظ أمن الخدمة ومشروعيتها. ولا تضمن <Brand /> استمرار الخدمة دون انقطاع، ولا خلوّها من الأخطاء، ولا توافقها المستمر مع جميع الأجهزة.</>,
                <>ولا يحدّ أيٌّ من نصوص هذه الشروط من أي حقٍّ يمنحه النظام السعودي للمستخدم ولا يجوز تقييده تعاقديًّا.</>,
            ],
        },
    },
    {
        id: "jurisdiction",
        en: {
            heading: "15. Saudi jurisdiction",
            body: [
                <>These Terms and any dispute arising in connection with the Service are governed by the laws of the Kingdom of Saudi Arabia, and any such dispute is subject to the exclusive jurisdiction of the competent Saudi courts, unless a mandatory rule of law provides otherwise.</>,
            ],
        },
        ar: {
            heading: "١٥. الاختصاص القضائي السعودي",
            body: [
                <>تخضع هذه الشروط، وأي نزاعٍ ينشأ في سياق الخدمة، لأنظمة المملكة العربية السعودية، وينعقد الاختصاص الحصري بشأنها للمحاكم السعودية المختصّة، ما لم يقرّر نصٌّ آمرٌ خلاف ذلك.</>,
            ],
        },
    },
    {
        id: "contact",
        en: {
            heading: "16. Contact",
            body: [
                <>Questions about these Terms, and formal notices under them, may be sent to <a className="text-[var(--crewz-gold)] hover:underline" href={`mailto:${SUPPORT_EMAIL}?subject=Terms%20of%20Service`}>{SUPPORT_EMAIL}</a>. The <Link to="/contact" className="text-[var(--crewz-gold)] hover:underline">Contact</Link> page lists the additional support channels currently offered.</>,
            ],
        },
        ar: {
            heading: "١٦. التواصل",
            body: [
                <>يمكن إرسال الاستفسارات حول هذه الشروط، وأي إشعاراتٍ رسميةٍ بموجبها، إلى <a className="text-[var(--crewz-gold)] hover:underline" dir="ltr" href={`mailto:${SUPPORT_EMAIL}?subject=%D8%B4%D8%B1%D9%88%D8%B7%20%D8%A7%D9%84%D8%AE%D8%AF%D9%85%D8%A9`}>{SUPPORT_EMAIL}</a>. وتُدرَج قنوات الدعم الإضافية المتاحة حاليًا في صفحة <Link to="/ar/contact" className="text-[var(--crewz-gold)] hover:underline">التواصل</Link>.</>,
            ],
        },
    },
    {
        id: "changes",
        en: {
            heading: "17. Changes to these Terms",
            body: [
                <>Material changes to these Terms will be displayed prominently in the app and on the website. Where the law requires, updated agreement will be sought. Continued use of the Service after a change becomes effective indicates acceptance of the updated Terms, unless the user closes the account first.</>,
            ],
        },
        ar: {
            heading: "١٧. تعديلات هذه الشروط",
            body: [
                <>سيُعلَن عن أي تعديلاتٍ جوهريةٍ على هذه الشروط بشكلٍ ظاهرٍ داخل التطبيق وعلى الموقع. وستُطلَب موافقةٌ جديدةٌ حيثما يشترط النظام ذلك. ويُعدّ استمرار استخدام الخدمة بعد سريان التعديل قبولًا للشروط المُعدَّلة، ما لم يبادر المستخدم إلى إغلاق حسابه قبل ذلك.</>,
            ],
        },
    },
    {
        id: "language",
        en: {
            heading: "18. Language",
            body: [
                <>The Arabic and English versions of these Terms are intended to match. For Saudi regulatory interpretation, the Arabic text governs to the extent legally permitted.</>,
            ],
        },
        ar: {
            heading: "١٨. اللغة",
            body: [
                <>وُضِعت نسختا هذه الشروط العربية والإنجليزية لتتطابقا في المعنى. ولأغراض التفسير النظامي داخل المملكة، تسود النسخة العربية بالقدر الذي يُجيزه النظام.</>,
            ],
        },
    },
];

const COPY_EN = {
    overline: "Legal",
    title: <>Terms of Service</>,
    lead: <>The terms governing use of the <Brand /> website, the CrewZNatioN mobile application, and participation in <Brand /> gatherings.</>,
    draftBanner: "Draft — pending owner review. This document is prepared for legal review and is not yet final.",
    effective: EFFECTIVE_EN,
    controllerLine: <>Operator: {CONTROLLER_EN}. Operating from Riyadh, Kingdom of Saudi Arabia.</>,
    switchLabel: "العربية",
    metaTitle: "Terms of Service — CrewZNatioN",
    metaDesc:
        "CrewZNatioN Terms of Service — eligibility, account responsibilities, acceptable conduct, Garage and Marketplace responsibilities, event QR passes, intellectual property and Saudi jurisdiction. Draft, pending owner review.",
    backHome: "Back to CrewZNatioN",
    canonical: "https://www.crewznation.com/terms",
};

const COPY_AR = {
    overline: "قانوني",
    title: <>شروط الخدمة</>,
    lead: <>الشروط التي تحكم استخدام موقع <Brand />، وتطبيق CrewZNatioN للأجهزة الجوّالة، والمشاركة في فعاليات <Brand />.</>,
    draftBanner: "مسوّدة — قيد المراجعة من المالك. هذه الوثيقة معدّة للمراجعة القانونية ولم تُعتمَد بعد بصورتها النهائية.",
    effective: EFFECTIVE_AR,
    controllerLine: <>المُشغِّل: {CONTROLLER_AR}. تعمل من مدينة الرياض، المملكة العربية السعودية.</>,
    switchLabel: "English",
    metaTitle: "شروط الخدمة — CrewZNatioN",
    metaDesc:
        "شروط خدمة CrewZNatioN — الأهلية، ومسؤوليات الحساب، والسلوك المقبول، ومسؤوليات الجراج والسوق، وتصاريح QR للفعاليات، والملكية الفكرية، والاختصاص القضائي السعودي. مسوّدة قيد المراجعة.",
    backHome: "العودة إلى CrewZNatioN",
    canonical: "https://www.crewznation.com/ar/terms",
};

export default function Terms({ locale = "en" }) {
    const isAr = locale === "ar";
    const Nav = isAr ? NavbarAr : Navbar;
    const Foot = isAr ? FooterAr : Footer;
    const t = isAr ? COPY_AR : COPY_EN;
    const other = isAr ? "/terms" : "/ar/terms";

    useEffect(() => {
        const prevTitle = document.title;
        document.title = t.metaTitle;

        let m = document.querySelector('meta[name="description"]');
        const prevDesc = m ? m.getAttribute("content") : null;
        if (!m) {
            m = document.createElement("meta");
            m.setAttribute("name", "description");
            document.head.appendChild(m);
        }
        m.setAttribute("content", t.metaDesc);

        let c = document.querySelector('link[rel="canonical"]');
        const prevCanonical = c ? c.getAttribute("href") : null;
        if (!c) {
            c = document.createElement("link");
            c.setAttribute("rel", "canonical");
            document.head.appendChild(c);
        }
        c.setAttribute("href", t.canonical);

        return () => {
            document.title = prevTitle;
            if (m && prevDesc !== null) m.setAttribute("content", prevDesc);
            if (c) {
                if (prevCanonical !== null) c.setAttribute("href", prevCanonical);
                else c.parentNode?.removeChild(c);
            }
        };
    }, [t.metaTitle, t.metaDesc, t.canonical]);

    return (
        <div
            className={`min-h-screen bg-[var(--crewz-bg)] text-[var(--crewz-text)] ${isAr ? "font-arabic" : "font-body"}`}
            dir={isAr ? "rtl" : "ltr"}
            data-testid={isAr ? "terms-page-ar" : "terms-page"}
        >
            <Nav />

            {/* HEADER */}
            <section className="px-6 md:px-12 lg:px-20 pt-24 pb-10 max-w-4xl mx-auto">
                <div className={`flex items-center gap-3 mb-5 ${isAr ? "justify-end" : ""}`}>
                    <span className="gold-rule" />
                    <p className={`overline ${isAr ? "font-arabic !text-[22.04px] md:!text-[23.8px]" : ""}`}>
                        {t.overline}
                    </p>
                </div>
                <h1
                    className={`font-display ${isAr ? "text-4xl md:text-5xl !leading-[1.25]" : "text-5xl md:text-6xl leading-[0.98]"} text-[var(--crewz-text)] max-w-3xl`}
                    data-testid="terms-title"
                >
                    {t.title}
                </h1>
                <p
                    className={`${isAr ? "font-arabic text-[17px] md:text-[19px] leading-[1.95]" : "font-body text-lg leading-relaxed font-light"} text-[var(--crewz-text)]/85 mt-8 max-w-2xl`}
                    data-testid="terms-lead"
                >
                    {t.lead}
                </p>

                {/* Draft banner — clearly marked, gold-bordered */}
                <div
                    className={`mt-8 border border-[var(--crewz-gold)]/60 bg-[var(--crewz-gold)]/10 px-5 py-4 max-w-2xl ${isAr ? "text-right" : "text-left"}`}
                    data-testid="terms-draft-banner"
                >
                    <p
                        className={`${isAr ? "font-arabic text-[17px] md:text-[19px] leading-[1.85]" : "font-body text-base md:text-lg leading-relaxed"} text-[var(--crewz-gold)]`}
                    >
                        {t.draftBanner}
                    </p>
                </div>

                <p
                    className={`${isAr ? "font-arabic text-[17px] md:text-[19px] leading-[1.95]" : "font-body text-sm leading-relaxed"} text-[var(--crewz-text)]/60 mt-4 max-w-2xl`}
                    data-testid="terms-effective"
                >
                    {t.effective}
                </p>
                <p
                    className={`${isAr ? "font-arabic text-[17px] md:text-[19px] leading-[1.95]" : "font-body text-sm leading-relaxed"} text-[var(--crewz-text)]/60 mt-1 max-w-2xl`}
                >
                    {t.controllerLine}
                </p>

                {/* Language switch */}
                <div className={`mt-8 ${isAr ? "text-right" : "text-left"}`}>
                    <Link
                        to={other}
                        dir={isAr ? "ltr" : "rtl"}
                        className="inline-flex items-center gap-2 border border-[var(--crewz-gold)] text-[var(--crewz-gold)] hover:bg-[var(--crewz-gold)] hover:text-[var(--crewz-bg)] px-5 py-2 text-[12px] uppercase tracking-[0.22em] font-bold transition-colors duration-300"
                        data-testid="terms-lang-switch"
                        aria-label={isAr ? "Switch to English" : "التبديل إلى العربية"}
                    >
                        {t.switchLabel}
                    </Link>
                </div>
            </section>

            {/* BODY — 18 sections */}
            <section
                className="px-6 md:px-12 lg:px-20 pb-16 max-w-4xl mx-auto"
                data-testid="terms-body"
            >
                <div className="space-y-12">
                    {SECTIONS.map((s) => {
                        const c = isAr ? s.ar : s.en;
                        return (
                            <section key={s.id} id={s.id} data-testid={`terms-section-${s.id}`}>
                                <h2
                                    className={`${isAr ? "font-arabic text-2xl md:text-3xl leading-[1.4]" : "font-display text-2xl md:text-3xl leading-[1.15]"} text-[var(--crewz-text)] mb-5 font-bold`}
                                >
                                    {c.heading}
                                </h2>
                                {c.body?.map((p, i) => (
                                    <p
                                        key={i}
                                        className={`${isAr ? "font-arabic text-[17px] md:text-[19px] leading-[1.95]" : "font-body text-base md:text-lg leading-relaxed font-light"} text-[var(--crewz-text)]/85 mb-4 break-words`}
                                    >
                                        {p}
                                    </p>
                                ))}
                                {c.list && (
                                    <ul
                                        className={`${isAr ? "font-arabic text-[17px] md:text-[19px] leading-[1.95] pr-5 space-y-2 [list-style:disc]" : "font-body text-base md:text-lg leading-relaxed font-light pl-5 space-y-2 list-disc"} text-[var(--crewz-text)]/85 mt-2 mb-4 marker:text-[var(--crewz-gold)] break-words`}
                                    >
                                        {c.list.map((li, i) => (
                                            <li key={i}>{li}</li>
                                        ))}
                                    </ul>
                                )}
                                {c.footer?.map((p, i) => (
                                    <p
                                        key={`f${i}`}
                                        className={`${isAr ? "font-arabic text-[17px] md:text-[19px] leading-[1.95]" : "font-body text-base md:text-lg leading-relaxed font-light"} text-[var(--crewz-text)]/85 mt-2 break-words`}
                                    >
                                        {p}
                                    </p>
                                ))}
                            </section>
                        );
                    })}
                </div>

                {/* Cross-links */}
                <div className={`mt-16 flex flex-wrap gap-x-6 gap-y-2 ${isAr ? "justify-end" : ""}`}>
                    <Link
                        to={isAr ? "/ar/privacy" : "/privacy"}
                        className="text-[var(--crewz-gold)] hover:opacity-80 transition-opacity underline-offset-4 hover:underline text-base"
                        data-testid="terms-privacy-link"
                    >
                        {isAr ? "سياسة الخصوصية" : "Privacy Policy"}
                    </Link>
                    <Link
                        to={isAr ? "/ar/rules" : "/rules"}
                        className="text-[var(--crewz-gold)] hover:opacity-80 transition-opacity underline-offset-4 hover:underline text-base"
                        data-testid="terms-rules-link"
                    >
                        {isAr ? "الأنظمة واللوائح" : "Rules & Regulations"}
                    </Link>
                    <Link
                        to={isAr ? "/ar/account-deletion" : "/account-deletion"}
                        className="text-[var(--crewz-gold)] hover:opacity-80 transition-opacity underline-offset-4 hover:underline text-base"
                        data-testid="terms-account-deletion-link"
                    >
                        {isAr ? "حذف الحساب" : "Account Deletion"}
                    </Link>
                    <Link
                        to={isAr ? "/ar" : "/"}
                        className="text-[var(--crewz-gold)] hover:opacity-80 transition-opacity underline-offset-4 hover:underline text-base"
                        data-testid="terms-back-home"
                    >
                        ← {t.backHome}
                    </Link>
                </div>
            </section>

            <Foot />
        </div>
    );
}
